import { Select } from '@creditornot/cb-select';
import { Duration } from 'luxon';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { createEnumParam, useQueryParam, withDefault } from 'use-query-params';

import { OfflineTimeInsight } from 'apis/merchant-gateway/insights';
import { Typography } from 'common/typography';
import { Legend, LegendChartType, VerticalBarChart } from 'modules/experience-core';
import { InsightEntity, InsightResponseWithPreviousInterval } from 'modules/insights-v2/types';
import { formatDuration, formatPercentage } from 'modules/insights-v2/utils';

import OfflineTimeLayout from './OfflineTimeLayout';
import { AlivenessMetricCategory, OfflineInterval } from './types';

type OfflineTimePerDayProps = {
  insightEntityType: InsightEntity['type'];
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
  offlineInterval: OfflineInterval;
  legends: Record<AlivenessMetricCategory, Legend>;
  setOfflineInterval: (offlineInterval: OfflineInterval) => void;
};

const ChartTypeSelector = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const SelectWrapper = styled.div`
  min-width: 208px;
`;

enum ChartCategory {
  UNAVAILABLE_HOURS = 'unavailableHours',
  PERCENT_UNAVAILABLE = 'percentUnavailable',
}

const chartCategoryToNameMap = {
  [ChartCategory.UNAVAILABLE_HOURS]: 'insights.offline-time.unavailable-hours',
  [ChartCategory.PERCENT_UNAVAILABLE]: 'insights.offline-time.percent-unavailable',
};

const chartCategoryList = Object.values(ChartCategory) as ChartCategory[];

const OfflineTimePerDay = ({
  insightEntityType,
  offlineTimeInsight,
  offlineInterval,
  legends,
  setOfflineInterval,
}: OfflineTimePerDayProps) => {
  const { formatMessage, locale } = useIntl();
  const data = offlineTimeInsight.selected.data?.data.offlineTime?.split;

  const [selectedCategory, setSelectedCategory] = useQueryParam(
    'selectedAlivenessCategory',
    withDefault(createEnumParam(chartCategoryList), ChartCategory.UNAVAILABLE_HOURS, true),
    {
      removeDefaultsFromUrl: true,
    },
  );

  const chartData = useMemo(() => {
    return (data ?? []).map(item => {
      const {
        label,
        total: {
          nonAliveMinutes,
          nonAliveTimePercentage,
          offlineMinutes,
          offlineTimePercentage,
          openingMinutes,
          unavailableMinutes,
          unavailablePercentage,
        },
      } = item;

      if (selectedCategory === ChartCategory.PERCENT_UNAVAILABLE) {
        return {
          [AlivenessMetricCategory.NON_ALIVE]: nonAliveTimePercentage,
          [AlivenessMetricCategory.OFFLINE_MANUALLY]: offlineTimePercentage,
          [AlivenessMetricCategory.AVAILABILITY]: 1 - unavailablePercentage,
          name: label || '',
        };
      } else {
        return {
          [AlivenessMetricCategory.NON_ALIVE]: nonAliveMinutes / 60,
          [AlivenessMetricCategory.OFFLINE_MANUALLY]: offlineMinutes / 60,
          [AlivenessMetricCategory.AVAILABILITY]: (openingMinutes - unavailableMinutes) / 60,
          name: label || '',
        };
      }
    });
  }, [data, selectedCategory]);

  const chartOptions = useMemo(
    () =>
      chartCategoryList.map(value => ({
        label: formatMessage({ id: chartCategoryToNameMap[value] }),
        value,
      })),
    [formatMessage],
  );

  const handleCategoryChange = (value: ChartCategory) => {
    setSelectedCategory(value);
  };

  return (
    <OfflineTimeLayout
      insightEntityType={insightEntityType}
      offlineInterval={offlineInterval}
      setOfflineInterval={setOfflineInterval}
      actions={
        <ChartTypeSelector>
          <Typography.Body3>
            <FormattedMessage id="insights.performance.show-chart-for" />
          </Typography.Body3>
          <SelectWrapper>
            <Select value={selectedCategory} options={chartOptions} onChange={handleCategoryChange} disableSearch />
          </SelectWrapper>
        </ChartTypeSelector>
      }
    >
      <VerticalBarChart
        isReverseBars
        legends={legends}
        isStacked
        data={chartData}
        axisIntegerOnly={{
          [LegendChartType.BAR]: selectedCategory === ChartCategory.UNAVAILABLE_HOURS,
        }}
        summaryKey={
          selectedCategory === ChartCategory.UNAVAILABLE_HOURS ? 'insights.offline-time.total-open-hours' : undefined
        }
        valueFormatter={
          selectedCategory === ChartCategory.UNAVAILABLE_HOURS
            ? hours => formatDuration(Duration.fromObject({ hours }))
            : ratio => formatPercentage(ratio, locale)
        }
      />
    </OfflineTimeLayout>
  );
};

export default OfflineTimePerDay;
